import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { useRef, useCallback, useState, useEffect } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Minus, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
  Undo, Redo, Upload, FolderOpen
} from 'lucide-react';
import axios from 'axios';
import MediaLibrary from './MediaLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-[#9E1B32] text-white' : 'text-slate-600 hover:bg-slate-100'
    } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ content, onChange, getToken, placeholder }) => {
  const fileRef = useRef(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const uploadRef = useRef(null);

  // Upload function stored in ref so paste handler always gets latest
  uploadRef.current = async (file) => {
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
    if (!allowed.includes(file.type)) return null;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken ? getToken() : null;
      const res = await axios.post(`${API_URL}/api/admin/media/upload-gcs`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      return res.data.url.startsWith('/') ? `${API_URL}${res.data.url}` : res.data.url;
    } catch (err) {
      console.error('Image upload failed:', err);
      return null;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Image.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#9E1B32] underline' } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || 'Start writing...' }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Check for image files in clipboard
        const files = Array.from(clipboardData.files || []);
        const imageFile = files.find(f => f.type.startsWith('image/'));

        if (!imageFile) {
          // Also check items for image blobs (screenshots, etc.)
          const items = Array.from(clipboardData.items || []);
          const imageItem = items.find(i => i.type.startsWith('image/'));
          if (imageItem) {
            const file = imageItem.getAsFile();
            if (file) {
              event.preventDefault();
              uploadRef.current(file).then(url => {
                if (url) {
                  const { state, dispatch } = view;
                  const node = state.schema.nodes.image.create({ src: url });
                  dispatch(state.tr.replaceSelectionWith(node));
                }
              });
              return true;
            }
          }
          return false;
        }

        event.preventDefault();
        uploadRef.current(imageFile).then(url => {
          if (url) {
            const { state, dispatch } = view;
            const node = state.schema.nodes.image.create({ src: url });
            dispatch(state.tr.replaceSelectionWith(node));
          }
        });
        return true;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const file = files[0];
        if (!file.type.startsWith('image/')) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        uploadRef.current(file).then(url => {
          if (url && coords) {
            const { state, dispatch } = view;
            const node = state.schema.nodes.image.create({ src: url });
            dispatch(state.tr.insert(coords.pos, node));
          }
        });
        return true;
      },
    },
  });

  // Convert any base64 images to uploaded URLs after paste
  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      const html = editor.getHTML();
      const base64Match = html.match(/src="data:image\/[^"]+"/);
      if (base64Match) {
        // Convert base64 to blob and upload
        const src = base64Match[0].slice(5, -1);
        fetch(src)
          .then(r => r.blob())
          .then(blob => {
            const file = new File([blob], 'pasted-image.png', { type: blob.type });
            return uploadRef.current(file);
          })
          .then(url => {
            if (url) {
              const newHtml = editor.getHTML().replace(/src="data:image\/[^"]+"/g, `src="${url}"`);
              editor.commands.setContent(newHtml, false);
            }
          });
      }
    };
    editor.on('update', handleUpdate);
    return () => editor.off('update', handleUpdate);
  }, [editor]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const url = await uploadRef.current(file);
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
    if (fileRef.current) fileRef.current.value = '';
  }, [editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  const addImageUrl = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-slate-50">
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 size={16} />
        </MenuButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
          <Bold size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
          <Italic size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
          <UnderlineIcon size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough size={16} />
        </MenuButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
          <List size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
          <ListOrdered size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
          <Quote size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
          <Minus size={16} />
        </MenuButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">
          <AlignLeft size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">
          <AlignCenter size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">
          <AlignRight size={16} />
        </MenuButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <MenuButton onClick={addLink} active={editor.isActive('link')} title="Add Link">
          <LinkIcon size={16} />
        </MenuButton>
        <MenuButton onClick={() => fileRef.current?.click()} title="Upload Image">
          <Upload size={16} />
        </MenuButton>
        <MenuButton onClick={() => setLibraryOpen(true)} title="Media Library">
          <FolderOpen size={16} />
        </MenuButton>
        <MenuButton onClick={addImageUrl} title="Image from URL">
          <ImageIcon size={16} />
        </MenuButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          <Undo size={16} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          <Redo size={16} />
        </MenuButton>
      </div>

      {/* Hidden file input for image upload */}
      <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none
          [&_.ProseMirror]:min-h-[280px] [&_.ProseMirror]:outline-none
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
          [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-4"
      />

      <MediaLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => { editor?.chain().focus().setImage({ src: url }).run(); }}
        getToken={getToken}
      />
    </div>
  );
};

export default RichTextEditor;
